import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralEditor, GeneralSelectMenu } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { networkCreateGameWiki, networkUpdateGameWiki } from 'src/network/game-management';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { schema } from './validationSchema';

const GameWikiForm = () => {
  const { gameId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { modal, closeModal } = useFormsModalStore();

  const { appInfo } = useAppInfoStore();

  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');

  const [gameWikiTypes, setGameWikiTypes] = useState([]);
  const [selectedGameWikiType, setSelectedGameWikiType] = useState('');

  const [editorData, setEditorData] = useState('');

  const gameWikiDetails = modal?.data;
  const defaultValues = getDefaultValues(gameWikiDetails);
  const isEditMode = gameWikiDetails;

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (appInfo) {
      const languages = generateSelectMenuItems(appInfo?.MainLanguage, 'MainLanguageTitle', 'MainLanguageID');
      const wikiTypes = generateSelectMenuItems(appInfo?.GameWikiType, 'tGameWikiTypeLable', 'GameWikiTypeID');

      setLanguages(languages);
      setSelectedLanguage(gameWikiDetails?.GameWikiLang || languages?.[0]?.id);

      setGameWikiTypes(wikiTypes);
      setSelectedGameWikiType(gameWikiDetails?.GameWikiGameWikiTypeID || wikiTypes?.[0]?.id);

      if (isEditMode) {
        setEditorData(gameWikiDetails?.GameWikiBody);
      }
    }
  }, [appInfo]);

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      GameWikiGameID: +gameId,
      GameWikiLang: selectedLanguage,
      GameWikiGameWikiTypeID: selectedGameWikiType,
      GameWikiIsShort: values?.GameWikiIsShort ? 1 : 0,
      GameWikiBody: editorData,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateGameWiki(+gameWikiDetails?.GameWikiID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('پیشنمایش موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries(['GAME_INFO', gameId]);
          } else {
            enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
          }
        }
      } else {
        const res = await networkCreateGameWiki(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('پیشنمایش موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries(['GAME_INFO', gameId]);
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
        setSelectedGameWikiType(gameWikiTypes?.[0]?.id);
        setSelectedLanguage(languages?.[0]?.id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="GameWikiTitle" type="text" label="عنوان" />

              <RHFTextField name="GameWikiSourceUrl" type="text" label="آدرس منبع" />

              <GeneralSelectMenu
                label="نوع دانشنامه"
                name="GameWikiGameWikiTypeID"
                options={gameWikiTypes}
                value={selectedGameWikiType}
                onChangeValue={setSelectedGameWikiType}
              />

              <GeneralSelectMenu
                label="زبان محتوا"
                name="GameWikiLang"
                options={languages}
                value={selectedLanguage}
                onChangeValue={setSelectedLanguage}
              />

              <RHFSwitch name="GameWikiIsShort" labelPlacement="end" label="این متن کوتاه است؟" />

              <div />
            </Box>

            <Typography variant="body1" sx={{ mt: 5, mb: 2 }}>
              محتوای دانش نامه
            </Typography>

            <div className="mt-6">
              <GeneralEditor editorData={editorData} setEditorData={setEditorData} />
            </div>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default GameWikiForm;
